import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
// import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $createHeadingNode,
    $createQuoteNode,
    $isHeadingNode,
} from '@lexical/rich-text';
import {
    $getSelectionStyleValueForProperty,
    $patchStyleText,
    $setBlocksType,
} from '@lexical/selection';
import {
    $createParagraphNode,
    $createTextNode,
    $getNodeByKey,
    $getSelection,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    REDO_COMMAND,
    UNDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { MenuItem, Button, ButtonGroup, Select, Box, Divider, Popover, TextField, Typography, CircularProgress } from "@mui/material";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import UploadIcon from '@mui/icons-material/Upload';
import styled from '@emotion/styled';
import { INSERT_IMAGE_COMMAND, dataUriToFile, inlineImageError } from './ImagesPlugin';
import { uploadInlineImage } from '../redux/ducks/attachment';
import { showAlert } from '../redux/slices/alerts';

const ToolBarBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderBottom: '1px solid',
    borderColor: theme.palette.divider,
    bgcolor: 'background.paper',
    color: 'text.secondary',
}))

const SpacedDivider = styled(Divider)(({ theme }) => ({
    alignSelf: 'stretch',
    height: 'auto',
    margin: theme.spacing(0, 0.5),
}))

const ImagePopoverBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    width: 340,
}))

const ImagePopoverHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}))

const HiddenFileInput = styled('input')({
    display: 'none',
})

const blockTypeToBlockName = {
    paragraph: 'Normal',
    h1: 'Heading 1',
    h2: 'Heading 2',
    h3: 'Heading 3',
    h4: 'Heading 4',
    h5: 'Heading 5',
    h6: 'Heading 6',
    quote: 'Quote',
};

function BlockFormatDropDown({ editor, blockType }) {
    const formatParagraph = () => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createParagraphNode());
            }
        });
    };

    const formatHeading = (headingSize) => {
        if (blockType !== headingSize) {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $setBlocksType(selection, () => $createHeadingNode(headingSize));
                }
            });
        }
    };

    const formatQuote = () => {
        if (blockType !== 'quote') {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $setBlocksType(selection, () => $createQuoteNode());
                }
            });
        }
    };

    return (
        <Select
            value={blockType}
            onChange={(event) => {
                const newBlockType = event.target.value;
                switch (newBlockType) {
                    case 'paragraph':
                        formatParagraph();
                        break;
                    case 'h1':
                    case 'h2':
                    case 'h3':
                    case 'h4':
                    case 'h5':
                    case 'h6':
                        formatHeading(newBlockType);
                        break;
                    case 'quote':
                        formatQuote();
                        break;
                    default:
                        break;
                }
            }}
            size="small"
        >
            {Object.entries(blockTypeToBlockName).map(([type, name]) => (
                <MenuItem key={type} value={type}>
                    {name}
                </MenuItem>
            ))}
        </Select>
    );
}

export default function ToolbarPlugin() {
    const [editor] = useLexicalComposerContext();
    const dispatch = useDispatch();
    const [activeEditor, setActiveEditor] = useState(editor);
    const [blockType, setBlockType] = useState('paragraph');
    const [fontSize, setFontSize] = useState('15px');
    const [isLink, setIsLink] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const fileInputRef = useRef(null);
    const isMountedRef = useRef(true);
    const [imageAnchorEl, setImageAnchorEl] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [linkAnchorEl, setLinkAnchorEl] = useState(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [isEditingLink, setIsEditingLink] = useState(false);
    const linkNodeKeyRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const fontSize = $getSelectionStyleValueForProperty(selection, 'font-size', '15px') || '15px';

            setFontSize(fontSize);

            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));

            const node = getSelectedNode(selection);
            const parent = node.getParent();
            setIsLink($isLinkNode(parent) || $isLinkNode(node));

            const anchorNode = selection.anchor.getNode();
            const element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = activeEditor.getElementByKey(elementKey);

            if (elementDOM !== null) {
                if ($isHeadingNode(element)) {
                    const tag = element.getTag();
                    setBlockType(tag);
                } else {
                    const type = element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type);
                    }
                }
            }
        }
    }, [activeEditor]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            1,
        );
    }, [editor, updateToolbar]);

    useEffect(() => {
        return editor.registerCommand(
            CAN_UNDO_COMMAND,
            (payload) => {
                setCanUndo(payload);
                return false;
            },
            1,
        );
    }, [editor]);

    useEffect(() => {
        return editor.registerCommand(
            CAN_REDO_COMMAND,
            (payload) => {
                setCanRedo(payload);
                return false;
            },
            1,
        );
    }, [editor]);

    const formatText = useCallback((formatType) => {
        activeEditor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                selection.formatText(formatType);
                setIsBold(selection.hasFormat('bold'));
                setIsItalic(selection.hasFormat('italic'));
                setIsUnderline(selection.hasFormat('underline'));
                setIsStrikethrough(selection.hasFormat('strikethrough'));
            }
        });
    }, [activeEditor]);

    const openLinkEditor = useCallback((event) => {
        editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const node = getSelectedNode(selection);
            const parent = node.getParent();
            const linkNode = $isLinkNode(parent) ? parent : $isLinkNode(node) ? node : null;

            if (linkNode) {
                setLinkUrl(linkNode.getURL());
                setLinkText(linkNode.getTextContent());
                setIsEditingLink(true);
                linkNodeKeyRef.current = linkNode.getKey();
            } else {
                setLinkUrl('');
                setLinkText(selection.getTextContent());
                setIsEditingLink(false);
                linkNodeKeyRef.current = null;
            }
        });
        setLinkAnchorEl(event.currentTarget);
    }, [editor]);

    const closeLinkEditor = useCallback(() => {
        linkNodeKeyRef.current = null;
        setLinkAnchorEl(null);
        setLinkUrl('');
        setLinkText('');
    }, []);

    const handleLinkSubmit = useCallback(() => {
        const url = linkUrl.trim();
        if (!url) return;
        const text = linkText.trim();

        if (isEditingLink && linkNodeKeyRef.current) {
            editor.update(() => {
                const linkNode = $getNodeByKey(linkNodeKeyRef.current);
                if (!linkNode || !$isLinkNode(linkNode)) return;
                linkNode.setURL(url);
                if (text && text !== linkNode.getTextContent()) {
                    const children = linkNode.getChildren();
                    children.forEach(c => c.remove());
                    linkNode.append($createTextNode(text));
                }
            });
        } else {
            let useToggle = false;
            editor.getEditorState().read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                    const selectedText = selection.getTextContent();
                    if (!text || text === selectedText) useToggle = true;
                }
            });

            if (useToggle) {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
            } else {
                editor.update(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection)) return;
                    if (!selection.isCollapsed()) selection.removeText();
                    const linkNode = $createLinkNode(url);
                    linkNode.append($createTextNode(text || url));
                    selection.insertNodes([linkNode]);
                });
            }
        }

        closeLinkEditor();
    }, [editor, linkUrl, linkText, isEditingLink, closeLinkEditor]);

    const handleRemoveLink = useCallback(() => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        closeLinkEditor();
    }, [editor, closeLinkEditor]);

    const uploadAndInsertImage = useCallback((file) => {
        const problem = inlineImageError(file);
        if (problem) {
            dispatch(showAlert({ severity: 'error', message: problem }));
            if (isMountedRef.current) setIsUploadingImage(false);
            return;
        }

        setIsUploadingImage(true);
        dispatch(uploadInlineImage({
            file,
            callback: (data) => {
                if (!isMountedRef.current) return;
                setIsUploadingImage(false);
                if (!data?.url) return;
                activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, data.url);
                setImageUrl('');
                setImageAnchorEl(null);
            },
        }));
    }, [activeEditor, dispatch]);

    const handleImageFileChange = useCallback((event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        uploadAndInsertImage(file);
    }, [uploadAndInsertImage]);

    const handleInsertImageUrl = useCallback(() => {
        const url = imageUrl.trim();
        if (!url) return;

        if (/^data:/i.test(url)) {
            setIsUploadingImage(true);
            const file = dataUriToFile(url, 'pasted-image');
            if (!file) {
                if (isMountedRef.current) setIsUploadingImage(false);
                dispatch(showAlert({ severity: 'error', message: 'That image could not be read. Try using "Upload a picture" instead.' }));
                return;
            }
            uploadAndInsertImage(file);
            return;
        }

        if (!/^https?:\/\//i.test(url)) {
            dispatch(showAlert({ severity: 'error', message: 'That is not a valid image link. Paste a link that starts with https:// — or use "Upload a picture" to add an image from your computer.' }));
            return;
        }

        activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, url);
        setImageUrl('');
        setImageAnchorEl(null);
    }, [activeEditor, dispatch, imageUrl, uploadAndInsertImage]);

    return (
        <ToolBarBox>
            <ButtonGroup variant="outlined" size="small">
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
                    }}
                    disabled={!canUndo}
                >
                    <UndoIcon />
                </Button>
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(REDO_COMMAND, undefined);
                    }}
                    disabled={!canRedo}
                >
                    <RedoIcon />
                </Button>
            </ButtonGroup>

            <SpacedDivider orientation='vertical' flexItem />
            <BlockFormatDropDown editor={editor} blockType={blockType} />
            <SpacedDivider orientation='vertical' flexItem />

            <Select
                value={fontSize}
                onChange={(event) => {
                    const newFontSize = event.target.value;
                    setFontSize(newFontSize);
                    activeEditor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            $patchStyleText(selection, {
                                'font-size': newFontSize,
                            });
                        }
                    });
                }}
                size="small"
            >
                {['12px', '13px', '14px', '15px', '16px', '17px', '18px', '19px', '20px'].map((size) => (
                    <MenuItem key={size} value={size}>
                        {size}
                    </MenuItem>
                ))}
            </Select>
            <SpacedDivider orientation='vertical' flexItem />

            <ButtonGroup variant="outlined" size="small">
                <Button
                    onClick={() => formatText('bold')}
                    variant={isBold ? 'contained' : 'outlined'}
                    title="Bold"
                >
                    <FormatBoldIcon />
                </Button>
                <Button
                    onClick={() => formatText('italic')}
                    variant={isItalic ? 'contained' : 'outlined'}
                    title="Italic"
                >
                    <FormatItalicIcon />
                </Button>
                <Button
                    onClick={() => formatText('underline')}
                    variant={isUnderline ? 'contained' : 'outlined'}
                    title="Underline"
                >
                    <FormatUnderlinedIcon />
                </Button>
                <Button
                    onClick={() => formatText('strikethrough')}
                    variant={isStrikethrough ? 'contained' : 'outlined'}
                    title="Strikethrough"
                >
                    <StrikethroughSIcon />
                </Button>
                <Button
                    onClick={openLinkEditor}
                    variant={isLink ? 'contained' : 'outlined'}
                    title="Insert Link"
                >
                    <LinkIcon />
                </Button>
            </ButtonGroup>
            <SpacedDivider orientation='vertical' flexItem />
            <ButtonGroup variant="outlined" size="small">
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                    }}
                    title="Insert List"
                >
                    <FormatListBulletedIcon />
                </Button>
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                    }}
                    title="Insert Numbered List"
                >
                    <FormatListNumberedIcon />
                </Button>
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
                    }}
                    title="Outdent (Shift+Tab)"
                >
                    <FormatIndentDecreaseIcon />
                </Button>
                <Button
                    onClick={() => {
                        activeEditor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
                    }}
                    title="Indent — nests the current bullet under the one above (Tab)"
                >
                    <FormatIndentIncreaseIcon />
                </Button>
            </ButtonGroup>
            <SpacedDivider orientation='vertical' flexItem />
            <ButtonGroup variant="outlined" size="small">
                <Button
                    onClick={(event) => setImageAnchorEl(event.currentTarget)}
                    title="Add an image — you can also paste or drag one straight into the editor"
                >
                    <AddPhotoAlternateIcon />
                </Button>
            </ButtonGroup>

            <HiddenFileInput ref={fileInputRef} type="file" accept="image/*" onChange={handleImageFileChange} />

            <Popover
                open={Boolean(imageAnchorEl)}
                anchorEl={imageAnchorEl}
                onClose={() => setImageAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <ImagePopoverBox>
                    <Typography variant="subtitle2">Add an image</Typography>
                    <ImagePopoverHint variant="caption">
                        Upload a picture from your computer, or insert one that is already online. You can also paste or drag an image straight into the editor.
                    </ImagePopoverHint>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isUploadingImage ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                        disabled={isUploadingImage}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {isUploadingImage ? 'Uploading…' : 'Upload a picture'}
                    </Button>
                    <TextField
                        label="Or paste an image link"
                        placeholder="https://…"
                        helperText="A linked image is loaded from the other website every time someone opens this, so that site can see who looked at it. Uploading is the safer choice."
                        size="small"
                        fullWidth
                        disabled={isUploadingImage}
                        value={imageUrl}
                        onChange={(event) => setImageUrl(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleInsertImageUrl();
                            }
                        }}
                    />
                    <Button variant="outlined" size="small" disabled={isUploadingImage || !imageUrl.trim()} onClick={handleInsertImageUrl}>
                        Insert image link
                    </Button>
                </ImagePopoverBox>
            </Popover>

            <Popover
                open={Boolean(linkAnchorEl)}
                anchorEl={linkAnchorEl}
                onClose={closeLinkEditor}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <ImagePopoverBox>
                    <Typography variant="subtitle2">
                        {isEditingLink ? 'Edit link' : 'Insert link'}
                    </Typography>
                    <TextField
                        label="URL"
                        placeholder="https://…"
                        size="small"
                        fullWidth
                        autoFocus
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleLinkSubmit();
                            }
                        }}
                    />
                    <TextField
                        label="Display text"
                        placeholder="Text to show"
                        size="small"
                        fullWidth
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleLinkSubmit();
                            }
                        }}
                        helperText="Leave blank to show the URL"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!linkUrl.trim()}
                            onClick={handleLinkSubmit}
                        >
                            {isEditingLink ? 'Update link' : 'Insert link'}
                        </Button>
                        {isEditingLink && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<LinkOffIcon />}
                                onClick={handleRemoveLink}
                            >
                                Remove
                            </Button>
                        )}
                    </Box>
                </ImagePopoverBox>
            </Popover>
        </ToolBarBox>
    );
}

function getSelectedNode(selection) {
    const anchor = selection.anchor;
    const focus = selection.focus;
    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();
    if (anchorNode === focusNode) {
        return anchorNode;
    }
    const isBackward = selection.isBackward();
    if (isBackward) {
        return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
        return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
    }
}

function $isAtNodeEnd(point) {
    return point.offset === point.getNode().getTextContent().length;
}
